<script setup lang="ts">
import { computed, h, ref } from 'vue';
import type { VNode } from 'vue';
import { NButton, NForm, NFormItem, NInput, useModal } from 'naive-ui';
import { SwitchOrg, fetchDeleteUser, fetchGetUserList, fetchLogout, fetchResetUserPassword } from '@/service/api';
import { useAuthStore } from '@/store/modules/auth';
import { useOrgStore } from '@/store/modules/org';
import { useTabStore } from '@/store/modules/tab';
import { useRouterPush } from '@/hooks/common/router';
import { useSvgIcon } from '@/hooks/common/icon';
import { localStg } from '@/utils/storage';
import { $t } from '@/locales';
import OrgSelector from '@/components/OrgSelector/index.vue';
defineOptions({
  name: 'UserAvatar'
});

const authStore = useAuthStore();
const orgStore = useOrgStore();
const { routerPushByKey, toLogin } = useRouterPush();
const { SvgIconVNode } = useSvgIcon();
const tabStore = useTabStore();
// 组织选择弹窗
const showOrgModal = ref(false);

function loginOrRegister() {
  toLogin();
}

type DropdownKey = 'user-center' | 'switch-org' | 'logout' | 'editPassword';

type DropdownOption =
  | {
      key: DropdownKey;
      label: string;
      icon?: () => VNode;
    }
  | {
      type: 'divider';
      key: string;
    };

const options = computed(() => {
  const opts: DropdownOption[] = [
    {
      label: $t('common.userCenter'),
      key: 'user-center',
      icon: SvgIconVNode({ icon: 'ph:user-circle', fontSize: 18 })
    },
    {
      type: 'divider',
      key: 'divider1'
    },
    {
      label: $t('common.switchOrg'),
      key: 'switch-org',
      icon: SvgIconVNode({ icon: 'ph:buildings', fontSize: 18 })
    },
    {
      type: 'divider',
      key: 'divider2'
    },
    {
      label: '修改密码',
      key: 'editPassword',
      icon: SvgIconVNode({ icon: 'mingcute:pencil-line', fontSize: 18 })
    },
    {
      label: $t('common.logout'),
      key: 'logout',
      icon: SvgIconVNode({ icon: 'ph:sign-out', fontSize: 18 })
    }
  ];

  return opts;
});

function logout() {
  window.$dialog?.info({
    title: $t('common.tip'),
    content: $t('common.logoutConfirm'),
    positiveText: $t('common.confirm'),
    negativeText: $t('common.cancel'),
    onPositiveClick: () => {
      fetchLogout().then(res => {
        if (!res.error && res.data) {
          authStore.resetStore();
          orgStore.resetStore();
          // 清除本地已选择组织的bestSignAccount
          localStg.set('orgbestSignAccount', '');
          // 清除本地已选择组织的unifiedCode
          localStg.set('orgUnifiedCode', '');
        }
      });
    }
  });
}

// 切换组织处理
function handleSwitchOrg() {
  showOrgModal.value = true;
}

// 组织选择确认处理
async function handleOrgConfirm(
  id: string,
  name: string,
  orgCode: string,
  deptId?: string,
  deptName?: string,
  deptOrgCode?: string,
  bestSignAccount?: string,
  unifiedCode?: string
) {
  const code = deptOrgCode || orgCode;

  const { error, data } = await SwitchOrg(authStore.userInfo.id, code);
  if (!data) {
    window.$message?.error('切换组织失败');
  } else {
    window.$message?.success('切换组织成功');

    orgStore.setCurrentOrgCode(id, name, orgCode);
    orgStore.setCurrentDeptCode(deptId || '', deptName || '', deptOrgCode || '');
    localStg.set('orgbestSignAccount', bestSignAccount);
    // 将组织的unifiedCode存储在本地（提交采购合同时携带）
    localStg.set('orgUnifiedCode', unifiedCode || '');

    await tabStore.clearTabs();
    showOrgModal.value = false;
    window.location.reload();
  }
}

// 组织选择取消处理
function handleOrgCancel() {
  showOrgModal.value = false;
}

function handleDropdown(key: DropdownKey) {
  if (key === 'logout') {
    logout();
  } else if (key === 'switch-org') {
    handleSwitchOrg();
  } else if (key === 'editPassword') {
    handleResetPassword();
  } else {
    routerPushByKey(key);
  }
}
const modal = useModal();

const formModel = ref({
  password: ''
});
async function handleResetPassword(id: string) {
  const modalInst = modal.create({
    title: $t('page.manage.user.resetPwd'),
    preset: 'dialog',
    negativeText: $t('common.cancel'),
    positiveText: $t('common.confirm'),
    content: () =>
      h(
        NForm,
        {
          model: formModel.value,
          rules: {
            password: [
              {
                required: true,
                message: $t('page.manage.user.newPwdRequired'),
                trigger: ['input', 'blur']
              }
            ]
          }
        },
        () => [
          h(
            NFormItem,
            {
              path: 'password',
              label: $t('page.manage.user.inputNewPwd'),
              labelPlacement: 'left',
              labelWidth: 100
            },
            () =>
              h(NInput, {
                type: 'password',
                showPasswordOn: 'click',
                placeholder: $t('page.manage.user.inputNewPwd'),
                value: formModel.value.password,
                onUpdateValue: (value: string) => {
                  formModel.value.password = value;
                }
              })
          )
        ]
      ),
    onPositiveClick: async () => {
      try {
        const { error } = await fetchResetUserPassword(localStg.get('userId'), formModel.value.password);
        if (!error) {
          window.$message?.success($t('page.manage.user.resetPwdSuccess'));
          modalInst.destroy();
          return true;
        }
        window.$message?.error($t('page.manage.user.resetPwdFailed'));
        return false;
      } catch (err) {
        window.$message?.error($t('page.manage.user.resetPwdFailed'));
        return false;
      }
    }
  });

  formModel.value.password = '';
}
</script>

<template>
  <NButton v-if="!authStore.isLogin" quaternary @click="loginOrRegister">
    {{ $t('page.login.common.loginOrRegister') }}
  </NButton>
  <NDropdown v-else placement="bottom" trigger="click" :options="options" @select="handleDropdown">
    <div>
      <ButtonIcon>
        <SvgIcon icon="ph:user-circle" class="text-icon-large" />
        <span class="text-16px font-medium">{{ authStore.userInfo.userName }}</span>
      </ButtonIcon>
    </div>
  </NDropdown>

  <!-- 组织选择弹窗 -->
  <OrgSelector v-model:show="showOrgModal" @confirm="handleOrgConfirm" @cancel="handleOrgCancel" />
</template>

<style scoped></style>
